// Rolling-window rate brake for cloud delete propagation.
//
// The watcher's onChanges callback emits batches every ~2 s. A per-batch
// brake (`if batch / manifestSize > 30%`) is trivial to slip on a real
// `rm -rf` because chokidar dribbles deletes out 5-10 at a time. Instead
// we sum deletes across the last 60 s and trip when the cumulative count
// exceeds 30% of the manifest. Legitimate single-file deletes via the
// agent stay nowhere near the threshold; a workspace unmount or
// catastrophic delete rapidly accumulates and is stopped.
//
// Manifest size of 0 means the agent has nothing in the cloud yet —
// can't be a disaster, so always allow.
//
// In-memory only. A plugin restart resets the window, which is acceptable:
// a restart already implies operator awareness.

const DEFAULT_WINDOW_MS = 60_000;
const DEFAULT_THRESHOLD_RATIO = 0.3;

export interface DeleteBrakeOptions {
  windowMs?: number;
  thresholdRatio?: number;
  now?: () => number;
}

export interface DeleteBrake {
  // Returns true if the proposed delete count is safe to proceed (and
  // records it). Returns false if combining it with the existing rolling
  // window would exceed the threshold — caller must not perform the
  // delete in that case.
  check(deleteCount: number, manifestSize: number): boolean;
  // Current sum of deletes within the active window (after expiry).
  windowSum(): number;
}

export function createDeleteBrake(opts: DeleteBrakeOptions = {}): DeleteBrake {
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const thresholdRatio = opts.thresholdRatio ?? DEFAULT_THRESHOLD_RATIO;
  const now = opts.now ?? (() => Date.now());

  const records: { ts: number; count: number }[] = [];

  function expire(): void {
    const cutoff = now() - windowMs;
    while (records.length > 0 && records[0].ts < cutoff) records.shift();
  }

  function sum(): number {
    return records.reduce((acc, r) => acc + r.count, 0);
  }

  return {
    check(deleteCount, manifestSize) {
      if (deleteCount <= 0) return true;
      expire();
      // Fresh agent (no manifest entries) — nothing to lose, always allow.
      if (manifestSize === 0) {
        records.push({ ts: now(), count: deleteCount });
        return true;
      }
      const projected = sum() + deleteCount;
      if (projected / manifestSize > thresholdRatio) return false;
      records.push({ ts: now(), count: deleteCount });
      return true;
    },
    windowSum() {
      expire();
      return sum();
    },
  };
}
